<template>
  <div class="v2-card v2-dashboard-orders">
    <div class="v2-card-header">
      <h3 class="v2-heading-sm">
        Orders
      </h3>
      <button
        class="v2-header-cta"
        type="button"
        @click="goToOrders"
      >
        Go to Orders
      </button>
    </div>

    <div
      v-if="isLoaded"
      class="v2-card-body"
    >
      <v2-table
        v-if="orders.length"
        :columns="tableColumns"
        :items="orders"
        :clickable-rows="true"
        class="v2-dashboard-orders-table"
        @row-click="goToOrderDetail"
      >
        <template #cell(orderSeqId)="{ item }">
          <span class="v2-text-nowrap v2-order-id">{{ item.orderSeqId }}</span>
        </template>

        <template #cell(createdAt)="{ item }">
          <span class="v2-text-nowrap">{{ formatDate(item.createdAt) }}</span>
        </template>

        <template #cell(time)="{ item }">
          <span class="v2-text-nowrap">{{ formatTime(item.createdAt) }}</span>
        </template>

        <template #cell(brandName)="{ item }">
          <span class="v2-text-nowrap">{{ item.brandName }}</span>
        </template>

        <template #cell(collectionName)="{ item }">
          <span>{{ item.collectionName }}</span>
        </template>

        <template #cell(status)="{ item }">
          <v2-badge :variant="statusVariant(item.status)">
            {{ statusLabel(item.status) }}
          </v2-badge>
        </template>

        <template #cell(notes)="{ item }">
          <span
            v-if="item.notes"
            :title="item.notes"
            class="v2-dashboard-orders__notes"
          >{{ item.notes }}</span>
        </template>

        <template #cell(totalQuantity)="{ item }">
          <span>{{ item.totalQuantity }}</span>
        </template>

        <template #cell(totalPrice)="{ item }">
          <span class="v2-text-nowrap">{{ formatTotal(item) }}</span>
        </template>
      </v2-table>

      <div
        v-else
        class="v2-empty-state"
      >
        <div class="v2-empty-icon">
          <feather-icon
            icon="SearchIcon"
            size="22"
          />
        </div>
        <p class="v2-empty-text v2-text-sm">
          You don't have any orders yet. Explore through hundreds of brands and place your first order today!
        </p>
        <router-link
          v-if="isEnabledMarketplace"
          :to="{ name: 'explore' }"
          class="v2-empty-cta"
        >
          Explore
        </router-link>
        <span
          v-else
          class="v2-empty-cta v2-empty-cta-disabled"
          :title="marketplaceAccessDeniedMessage"
          aria-disabled="true"
          role="button"
        >
          Explore
        </span>
      </div>
    </div>

    <div
      v-else
      class="v2-loading"
    >
      <div
        class="v2-spinner"
        aria-label="Loading"
      />
    </div>
  </div>
</template>

<script>
import analytics from '@/@core/utils/analytics'
import constants, {
  marketplaceAccessDeniedMessage,
  ORDERS_TEXT_RETAILER,
} from '@/constants'
import { useDashboardOrders } from '@/composables/useDashboardOrders'
import { utils } from '@kingpin-global/kingpin-utils-frontend'
import { getDateFromTimestamp } from '@/common-utils'
import V2Table from '@/components/v2/V2Table.vue'
import V2Badge from '@/components/v2/V2Badge.vue'

const { formatCurrency } = utils

// Mirrors V2OrderList's variant mapping for retailer-side statuses.
const STATUS_VARIANT_MAP = {
  CANCELLED: 'destructive',
  REJECTED: 'destructive',
  CONFIRMED: 'success',
  ACCEPTED: 'success',
  DELIVERED: 'success',
  PROCESSED: 'success',
  MODIFIED: 'warning',
  PARTIALLY_CONFIRMED: 'primary',
}

export default {
  name: 'V2DashboardOrdersList',
  components: { V2Table, V2Badge },
  data() {
    return {
      marketplaceAccessDeniedMessage,
      tableColumns: [
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'createdAt', label: 'Order Date' },
        { key: 'time', label: 'Time' },
        { key: 'brandName', label: 'Brand' },
        { key: 'collectionName', label: 'Collection Name' },
        { key: 'status', label: 'Status' },
        { key: 'notes', label: 'Notes' },
        { key: 'totalQuantity', label: 'Total Units' },
        { key: 'totalPrice', label: 'Total Amount' },
      ],
    }
  },
  setup() {
    const page = useDashboardOrders()
    return { ...page }
  },
  mounted() {
    this.fetchOrders()
  },
  methods: {
    goToOrders() {
      this.$router.push({ name: 'v2-orders' })
      analytics.track(
        constants.TRACKS.ACTIONS.USER_CLICKS_GO_TO_ORDER_BTN_ON_DASHBOARD,
        this.profile,
      )
    },
    goToOrderDetail(order) {
      this.$router.push({
        name: 'v2-order/detail',
        params: { orderId: order._id },
      })
    },
    formatDate(ts) {
      return ts ? getDateFromTimestamp(ts) : ''
    },
    formatTime(ts) {
      if (!ts) return ''
      const date = new Date(ts)
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: tz,
      })
    },
    formatTotal(order) {
      const amount = order.totalPriceAfterDiscount || order.totalPrice || 0
      const currency = order.currency || order.orderCurrency || 'USD'
      return formatCurrency(amount, currency)
    },
    statusLabel(status) {
      return ORDERS_TEXT_RETAILER[status] || status || ''
    },
    statusVariant(status) {
      const key = (status || '').toUpperCase().replace(/\s+/g, '')
      return STATUS_VARIANT_MAP[key] || 'default'
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-dashboard-orders {
  display: flex;
  flex-direction: column;
}

.v2-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.25rem 0;
}

.v2-header-cta {
  background: transparent;
  border: 1px solid var(--v2-primary);
  color: var(--v2-primary);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.v2-header-cta:hover,
.v2-header-cta:focus-visible {
  background: var(--v2-primary);
  color: var(--v2-primary-foreground);
  outline: none;
}

.v2-card-body {
  padding: 1rem 1.25rem 1.25rem;
}

.v2-dashboard-orders-table {
  font-size: 0.875rem;
}

.v2-order-id {
  color: var(--v2-primary);
  font-weight: 500;
}

.v2-dashboard-orders__notes {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  color: var(--v2-muted-foreground);
}

.v2-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.v2-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--v2-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--v2-muted-foreground);
}

.v2-empty-text {
  margin: 0;
  color: var(--v2-muted-foreground);
  max-width: 420px;
}

.v2-empty-cta {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: var(--v2-primary);
  color: var(--v2-primary-foreground);
  border-radius: 8px;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  transition: opacity 0.15s;
}

.v2-empty-cta:hover,
.v2-empty-cta:focus-visible {
  opacity: 0.9;
  color: var(--v2-primary-foreground);
  text-decoration: none;
  outline: none;
}

.v2-empty-cta-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}

.v2-empty-cta-disabled:hover,
.v2-empty-cta-disabled:focus-visible {
  opacity: 0.5;
}

.v2-loading {
  padding: 2rem;
  display: flex;
  justify-content: center;
}

.v2-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--v2-border);
  border-top-color: var(--v2-primary);
  border-radius: 50%;
  animation: v2-spin 0.8s linear infinite;
}

@keyframes v2-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .v2-card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
